// src/pages/EntrepreneurshipEducationContent/ModuleContentPage.js
import React, { useState } from 'react';
import '../../styles/ModuleContentPage.css'; 
import { FaVideo, FaFilm, FaFlask, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// These modules would ideally be fetched or filtered based on currentScope in a real app
const allModules = {
  Local: [
    { id: 'local-accounting', title: 'Practical Accounting (Local)' },
    { id: 'local-economics', title: 'Local Market Economics' },
    { id: 'art-of-investment', title: 'Art of Investment (Local)' }, // Added scope for clarity
    { id: 'optimisation', title: 'Optimisation (Local)' }, // Added scope for clarity
  ],
  National: [
    { id: 'national-finance', title: 'National Business Finance' },
    { id: 'national-policy', title: 'SA Economic Policy Impact' },
    { id: 'art-of-investment', title: 'Art of Investment (National)' }, // Added scope for clarity
    { id: 'optimisation', title: 'Optimisation (National)' }, // Added scope for clarity
  ],
  Continental: [
    { id: 'continental-trade', title: 'African Trade Agreements' },
    { id: 'continental-logistics', title: 'Cross-Border Logistics' },
    { id: 'art-of-investment', title: 'Art of Investment (Continental)' }, // Added scope for clarity
    { id: 'optimisation', title: 'Optimisation (Continental)' }, // Added scope for clarity
  ],
  Global: [
    { id: 'global-marketing', title: 'International Marketing Strategies' },
    { id: 'global-supply-chain', title: 'Global Supply Chain Management' },
    { id: 'art-of-investment', title: 'Art of Investment (Global)' }, // Added scope for clarity
    { id: 'optimisation', title: 'Optimisation (Global)' }, // Added scope for clarity
  ],
};

// Accept currentScope and navigateToDashboardView as props
function ModuleContentPage({ currentScope, navigateToDashboardView }) { 
  const navigate = useNavigate();
  
  // Determine modules based on currentScope, default to Local or empty if scope is unexpected
  const modulesForCurrentScope = allModules[currentScope] || allModules['Local'] || [];
  
  const [activeModuleTitle, setActiveModuleTitle] = useState(
    modulesForCurrentScope.length > 0 ? modulesForCurrentScope[0].title : 'No Modules Available'
  ); 
  
  const handleBreadcrumbNav = (pathKey) => {
    if (!navigateToDashboardView) {
        console.warn("navigateToDashboardView prop not provided to ModuleContentPage");
        navigate('/dev-entrepreneurship-dashboard'); 
        return;
    }
    console.log(`Breadcrumb: Navigating to ${pathKey} for scope ${currentScope}`);
    if (pathKey === 'scopeSelectionOverview') {
        navigateToDashboardView('scopeSelectionOverview'); 
    } else if (pathKey === 'currentScopeFunctionalHome') {
        // This navigates to the learning content view for the current scope
        navigateToDashboardView('learningContent', currentScope);
    }
  };

  const handleModuleChange = (moduleTitle, moduleId) => {
    setActiveModuleTitle(moduleTitle);
    console.log(`Changing to module: ${moduleTitle} (ID: ${moduleId}) within scope: ${currentScope}`);
  };
  
  return (
    <div className="module-content-page p-4 md:p-6 bg-gray-100 min-h-screen font-sans">
      {/* Breadcrumb container with mb-8 for padding underneath */}
      <div className="breadcrumb bg-white p-3 rounded-lg shadow-md mb-8 text-sm">
        <button 
            className="breadcrumb-link text-indigo-600 hover:underline" 
            onClick={() => handleBreadcrumbNav('scopeSelectionOverview')}
        >
          Scope Selection
        </button>
        <span className="mx-2 text-gray-400">&gt;</span>
        <button 
            className="breadcrumb-link text-indigo-600 hover:underline" 
            onClick={() => handleBreadcrumbNav('currentScopeFunctionalHome')}
        >
          {currentScope ? `${currentScope} Entrepreneurship` : 'Selected Scope'}
        </button>
        <span className="mx-2 text-gray-400">&gt;</span>
        <span className="active-breadcrumb text-gray-700 font-medium">{activeModuleTitle}</span>
      </div>

      <div className="content-area flex flex-col md:flex-row gap-6">
        <div className="module-sidebar md:w-1/4 bg-white p-4 rounded-lg shadow-md">
          <h3 className="sidebar-title text-xl font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-3">
            MODULES ({currentScope})
          </h3>
          {modulesForCurrentScope.length > 0 ? modulesForCurrentScope.map((module) => (
            <button 
              key={module.id}
              className={`module-button w-full text-left p-3 mb-2 rounded-md transition-colors duration-150 ease-in-out text-gray-700 hover:bg-red-50 focus:outline-none focus:bg-red-100 ${activeModuleTitle === module.title ? "bg-red-100 font-semibold border-l-4 border-red-500" : "border-l-4 border-transparent"}`}
              onClick={() => handleModuleChange(module.title, module.id)}
            >
              {module.title}
            </button>
          )) : <p className="text-gray-500">No modules available for this scope.</p>}
        </div>

        <div className="content-grid flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="content-card bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out flex flex-col items-center text-center">
            <div className="card-icon mb-4"><FaVideo size={48} className="text-indigo-600" /></div>
            <h4 className="card-title text-xl font-semibold text-gray-800 mb-2">Human Videos</h4>
            <p className="card-description text-gray-600 text-sm mb-4">Learn from experts explaining concepts and demonstrating experiments.</p>
            <button className="mt-auto px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-full sm:w-auto">
              Watch Videos
            </button>
          </div>

          <div className="content-card bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out flex flex-col items-center text-center">
            <div className="card-icon mb-4"><FaFilm size={48} className="text-sky-500" /></div>
            <h4 className="card-title text-xl font-semibold text-gray-800 mb-2">Animated Videos</h4>
            <p className="card-description text-gray-600 text-sm mb-4">Engaging 2D and 3D animations to bring STEM topics to life.</p>
            <button className="mt-auto px-5 py-2 bg-sky-500 text-white text-sm font-medium rounded-md hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 w-full sm:w-auto">
              Explore Animations
            </button>
          </div>

          <div className="content-card bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out flex flex-col items-center text-center">
            <div className="card-icon mb-4"><FaFlask size={48} className="text-green-500" /></div>
            <h4 className="card-title text-xl font-semibold text-gray-800 mb-2">Virtual Labs</h4>
            <p className="card-description text-gray-600 text-sm mb-4">Simulated experiments for safe, hands-on exploration.</p>
            <button className="mt-auto px-5 py-2 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 w-full sm:w-auto">
              Enter Lab
            </button>
          </div>

          <div className="content-card bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 ease-in-out flex flex-col items-center text-center">
            <div className="card-icon mb-4"><FaUsers size={48} className="text-amber-500" /></div>
            <h4 className="card-title text-xl font-semibold text-gray-800 mb-2">Virtual Debate</h4>
            <p className="card-description text-gray-600 text-sm mb-4">Group debates competition to discuss factually blurred concepts/standards.</p>
            <button className="mt-auto px-5 py-2 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 w-full sm:w-auto">
              Join Debate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModuleContentPage;
